import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TablePagination
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  LocalHospital as LocalHospitalIcon,
  PersonAdd as PersonAddIcon,
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime
} from '@mui/icons-material';
import axios from 'axios';
import { getLabSummaryToday, getLabRequestsToday } from '../BaseURL';

const FinalHome = ({ onEditRequest }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inPatientCount, setInPatientCount] = useState(0);
  const [outPatientCount, setOutPatientCount] = useState(0);
  const [todayRequests, setTodayRequests] = useState(0);
  const [completionRate, setCompletionRate] = useState(0);
  const [labResultsData, setLabResultsData] = useState([]);
  const [requestedTestsData, setRequestedTestsData] = useState([]);
  const [labRequestsList, setLabRequestsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Beautiful gradient colors for charts
  const CHART_COLORS = {
    requested: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe'],
    completed: ['#11998e', '#38ef7d', '#56ab2f', '#a8e063', '#20bf55']
  };

  useEffect(() => {
    fetchLabSummary();
    fetchLabRequestsToday();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const fetchLabSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(getLabSummaryToday);
      
      if (response.data.success) {
        const summary = response.data.summary;
        
        // Update patient stats
        setInPatientCount(summary.patientStats.inpatientsActive || 0);
        setOutPatientCount(summary.patientStats.outpatientsToday || 0);
        setTodayRequests(summary.labStats.totalRequests || 0);
        
        // Calculate completion rate (results entered / requests made)
        const totalRequests = summary.labStats.totalRequests || 0;
        const totalResults = summary.labStats.totalResults || 0;
        const rate = totalRequests > 0 ? Math.round((totalResults / totalRequests) * 100) : 0;
        setCompletionRate(rate);
        
        // Convert completed tests to chart format
        if (summary.labStats.completedTests && summary.labStats.completedTests.length > 0) {
          const chartData = summary.labStats.completedTests.map(test => ({
            name: test.test,
            count: test.resultCount
          }));
          setLabResultsData(chartData);
        }

        // Convert requested tests to chart format
        if (summary.labStats.requestedTests && summary.labStats.requestedTests.length > 0) {
          const chartData = summary.labStats.requestedTests.map(test => ({
            name: test.lab || test.test,
            count: test.testCount
          }));
          setRequestedTestsData(chartData);
        }
      }
    } catch (error) {
      console.error('Error fetching lab summary:', error);
      setError('Failed to load lab summary data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabRequestsToday = async () => {
    try {
      const response = await axios.get(getLabRequestsToday);
      if (response.data.success) {
        setLabRequestsList(response.data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching lab requests:', error);
    }
  };

  const handleEditRequest = (request) => {
    if (onEditRequest) {
      onEditRequest({
        patientId: request.id,
        testName: request.lab,
        timestamp: new Date().getTime()
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 2
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {payload[0].value} tests
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, icon: Icon, gradient, accent }) => (
    <Card sx={{
      background: 'white',
      border: '1px solid #e8f5e9',
      borderRadius: 2.5,
      boxShadow: '0 2px 10px rgba(17, 153, 142, 0.06)',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        background: gradient
      },
      '&:hover': {
        boxShadow: '0 6px 20px rgba(17, 153, 142, 0.15)',
        transform: 'translateY(-2px)',
        borderColor: '#c8e6c9'
      }
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 4px 10px ${accent}40`
          }}
        >
          <Icon sx={{ fontSize: 22, color: 'white' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#607d8b',
              mb: 0.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: '#1b5e20',
              lineHeight: 1.1
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#11998e' }} /> : value.toLocaleString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
      p: 2.5
    }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: 2.5,
          background: 'white',
          border: '1px solid #c8e6c9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(17, 153, 142, 0.35)'
            }}
          >
            <ScienceIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.1
              }}
            >
              Laboratory Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.25 }}>
              Today's Lab Operations Overview
            </Typography>
          </Box>
        </Box>
        <Chip
          label={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
          icon={<AccessTime sx={{ fontSize: 16, color: '#11998e !important' }} />}
          sx={{
            bgcolor: '#e8f5e9',
            color: '#1b5e20',
            fontWeight: 600,
            border: '1px solid #c8e6c9',
            borderRadius: 1.5
          }}
        />
      </Paper>

      {/* Statistics Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)'
        },
        gap: 2,
        mb: 2.5
      }}>
        <StatCard
          title="In-Patient Count"
          value={inPatientCount}
          icon={LocalHospitalIcon}
          accent="#0e8679"
          gradient="linear-gradient(135deg, #0e8679 0%, #11998e 100%)"
        />
        <StatCard
          title="Out-Patient Count"
          value={outPatientCount}
          icon={PersonAddIcon}
          accent="#11998e"
          gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
        />
        <StatCard
          title="Today's Requests"
          value={todayRequests}
          icon={ScienceIcon}
          accent="#2ed968"
          gradient="linear-gradient(135deg, #38ef7d 0%, #56ab2f 100%)"
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Charts Section */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        },
        gap: 2,
        mb: 2.5
      }}>
        {/* Requested Tests */}
        <Paper sx={{
          p: 2,
          borderRadius: 2.5,
          boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
          background: 'white',
          border: '1px solid #e8f5e9',
          transition: 'all 0.25s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(17, 153, 142, 0.12)',
            borderColor: '#c8e6c9'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(17, 153, 142, 0.35)'
                }}
              >
                <ScienceIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
                  Tests Requested Today
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {requestedTestsData.length} test type{requestedTestsData.length === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${requestedTestsData.reduce((sum, item) => sum + item.count, 0)} requests`}
              size="small"
              sx={{
                bgcolor: '#e8f5e9',
                color: '#1b5e20',
                fontWeight: 700,
                border: '1px solid #c8e6c9',
                fontSize: '0.7rem'
              }}
            />
          </Box>

          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={requestedTestsData}
              margin={{ top: 5, right: 5, left: -20, bottom: 50 }}
            >
              <defs>
                {requestedTestsData.map((entry, index) => (
                  <linearGradient key={`gradient-${index}`} id={`colorRequested${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.requested[index % CHART_COLORS.requested.length]} stopOpacity={1} />
                    <stop offset="100%" stopColor={CHART_COLORS.requested[index % CHART_COLORS.requested.length]} stopOpacity={0.6} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-35}
                textAnchor="end"
                height={70}
                tick={{ fill: '#666', fontSize: 11, fontWeight: 500 }}
              />
              <YAxis
                tick={{ fill: '#666', fontSize: 11, fontWeight: 500 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {requestedTestsData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#colorRequested${index})`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        {/* Completed Tests */}
        <Paper sx={{
          p: 2,
          borderRadius: 2.5,
          boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
          background: 'white',
          border: '1px solid #e8f5e9',
          transition: 'all 0.25s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(17, 153, 142, 0.12)',
            borderColor: '#c8e6c9'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(17, 153, 142, 0.35)'
                }}
              >
                <TrendingUpIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
                  Tests Completed Today
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {labResultsData.length} test type{labResultsData.length === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${labResultsData.reduce((sum, item) => sum + item.count, 0)} results`}
              size="small"
              sx={{
                bgcolor: '#e8f5e9',
                color: '#1b5e20',
                fontWeight: 700,
                border: '1px solid #c8e6c9',
                fontSize: '0.7rem'
              }}
            />
          </Box>

          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={labResultsData}
              margin={{ top: 5, right: 5, left: -20, bottom: 50 }}
            >
              <defs>
                {labResultsData.map((entry, index) => (
                  <linearGradient key={`gradient-complete-${index}`} id={`colorCompleted${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.completed[index % CHART_COLORS.completed.length]} stopOpacity={1} />
                    <stop offset="100%" stopColor={CHART_COLORS.completed[index % CHART_COLORS.completed.length]} stopOpacity={0.6} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                angle={-35}
                textAnchor="end"
                height={70}
                tick={{ fill: '#666', fontSize: 11, fontWeight: 500 }}
              />
              <YAxis
                tick={{ fill: '#666', fontSize: 11, fontWeight: 500 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {labResultsData.map((entry, index) => (
                  <Cell
                    key={`cell-complete-${index}`}
                    fill={`url(#colorCompleted${index})`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Lab Requests Table */}
      <Paper sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        background: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(17, 153, 142, 0.35)'
              }}
            >
              <ScienceIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1b5e20' }}>
                Today's Lab Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and enter results for pending lab tests
              </Typography>
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search by Patient ID or Test..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <ScienceIcon sx={{ color: '#11998e', mr: 1, fontSize: 20 }} />
            }}
            sx={{
              minWidth: '320px',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f1fcf9',
                '& fieldset': {
                  borderColor: '#c8e6c9'
                },
                '&:hover fieldset': {
                  borderColor: '#11998e'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#11998e',
                  borderWidth: 2
                }
              }
            }}
          />
        </Box>

        {(() => {
          const filteredRequests = labRequestsList.filter(request =>
            !searchTerm ||
            (request.id && request.id.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
            (request.lab && request.lab.toLowerCase().includes(searchTerm.toLowerCase()))
          );
          const pagedRequests = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

          return (
            <>
              <TableContainer sx={{ borderRadius: 2, border: '1px solid #e8f5e9', overflow: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: 60 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Test Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Requested</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRequests.map((request, index) => {
                      const rowNumber = page * rowsPerPage + index + 1;
                      const requestDate = request.dat ? new Date(request.dat) : null;
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: index % 2 === 0 ? 'white' : '#f8fdfb',
                            '&:hover': {
                              backgroundColor: '#e8f5e9',
                              transition: 'background-color 0.2s ease'
                            },
                            borderBottom: '1px solid #e8f5e9'
                          }}
                        >
                          <TableCell sx={{ color: '#9e9e9e', fontWeight: 600, fontSize: '0.85rem' }}>
                            {rowNumber}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  flexShrink: 0
                                }}
                              >
                                {String(request.id || '').slice(-2).toUpperCase() || '—'}
                              </Box>
                              <Typography sx={{ fontWeight: 600, color: '#1b5e20' }}>
                                {request.id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.lab}
                              size="small"
                              sx={{
                                bgcolor: '#e8f5e9',
                                color: '#1b5e20',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                border: '1px solid #c8e6c9'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {requestDate ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#11998e' }}>
                                <AccessTime sx={{ fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#37474f' }}>
                                  {requestDate.toLocaleDateString()}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#9e9e9e' }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                                  boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
                                }
                              }}
                              onClick={() => handleEditRequest(request)}
                            >
                              Enter Results
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                          <Box sx={{
                            textAlign: 'center',
                            py: 6,
                            background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
                            border: '1px dashed #c8e6c9'
                          }}>
                            <ScienceIcon sx={{ fontSize: 48, color: '#11998e', opacity: 0.5, mb: 1 }} />
                            <Typography variant="h6" sx={{ mb: 0.5, color: '#1b5e20', fontWeight: 600 }}>
                              {searchTerm ? 'No matching requests' : 'No lab requests found'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm
                                ? 'Try a different patient ID or test name'
                                : 'Lab requests will appear here when they are created'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredRequests.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  borderTop: '1px solid #e8f5e9',
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontWeight: 500,
                    color: '#37474f'
                  },
                  '.MuiTablePagination-select': {
                    color: '#11998e',
                    fontWeight: 600
                  }
                }}
              />

              {filteredRequests.length > 0 && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'linear-gradient(135deg, #11998e10 0%, #38ef7d10 100%)',
                  border: '1px solid #c8e6c9'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#11998e' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                      Showing {pagedRequests.length} of {filteredRequests.length} request{filteredRequests.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  {searchTerm && (
                    <Typography variant="caption" sx={{ color: '#11998e', fontWeight: 600 }}>
                      Filtered by "{searchTerm}"
                    </Typography>
                  )}
                </Box>
              )}
            </>
          );
        })()}
      </Paper>
    </Box>
  );
};

export default FinalHome;